import React from 'react';
import PropTypes from 'prop-types';
import { Button, Collapse, useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';

// UTILITY
import { scrollToRef } from '@/utility/common';

// COMPONENTS
import SectionTitleLink from '@/components/SectionTitleLink';

// STYLES
import { Container } from './styles';

const CollapsibleSection = ({
  children,
  in: open,
  variant,
  title,
  disableRounding,
  showShadow,
  onClick,
  ButtonProps,
  ...props
}) => {
  const ref = React.useRef(null);
  const interacted = React.useRef(false);
  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));
  const buttonColor = React.useMemo(
    () => (variant === 'blue' || variant === 'purple' ? 'default' : 'primary'),
    [variant],
  );

  React.useEffect(() => {
    if (open && !interacted.current) interacted.current = true;
  }, [open]);

  const handleButtonClick = React.useCallback(
    e => {
      e.stopPropagation();
      if (ButtonProps.onClick) ButtonProps.onClick();
    },
    [ButtonProps.onClick],
  );

  const handleClick = React.useCallback(
    e => {
      e.stopPropagation();
      onClick();
    },
    [onClick],
  );

  const handleOnEntered = React.useCallback(() => {
    scrollToRef(ref, -120);
  }, [ref]);

  return (
    <Container
      ref={ref}
      className={clsx(
        'collapsible-section',
        variant && `collapsible-section--${variant}`,
        disableRounding && 'collapsible-section--disable-rounding',
        open && 'collapsible-section--opened',
        showShadow && !open && 'collapsible-section--shadow',
        !open && 'collapsible-section--cursor-pointer',
      )}
      onClick={handleClick}
    >
      <Collapse
        in={open}
        collapsedSize={downXs ? 100 : 160}
        {...props}
        onClick={handleClick}
        onEntered={handleOnEntered}
        onEntering={() => (interacted.current = true)}
        onExiting={() => (interacted.current = true)}
      >
        <div
          className={clsx(
            'collapsible-section__collapsed-content',
            !interacted.current && open && 'collapsible-section--hide',
            interacted.current &&
              (!open ? 'collapsible-section--fadein' : 'collapsible-section--fadeout'),
          )}
        >
          <SectionTitleLink
            className="collapsible-section__title m-r-15"
            title={title}
            link=" "
            TypoProps={{ component: 'h4' }}
          />

          {!downXs && (
            <Button
              className="collapsible-section__button"
              variant="contained"
              color={buttonColor}
              fullWidth
              {...ButtonProps}
              onClick={handleButtonClick}
            />
          )}
        </div>
        <div
          className={clsx(
            'collapsible-section__expanded-content',
            !interacted.current && !open && 'collapsible-section--hide',
            interacted.current &&
              (open ? 'collapsible-section--fadein' : 'collapsible-section--fadeout'),
          )}
        >
          {children}
        </div>
      </Collapse>
    </Container>
  );
};

CollapsibleSection.defaultProps = {
  variant: 'white',
  disableRounding: false,
  showShadow: false,
  ButtonProps: {},
  onClick: () => {},
};

CollapsibleSection.propTypes = {
  in: PropTypes.bool.isRequired,
  children: PropTypes.element,
  variant: PropTypes.string,
  title: PropTypes.string.isRequired,
  disableRounding: PropTypes.bool,
  showShadow: PropTypes.bool,
  onClick: PropTypes.func,
  ButtonProps: PropTypes.object,
};

export default CollapsibleSection;
